import { isValidEtag } from "./multipartValidation";
import type { OwnershipWriteOutcome, UploadLease } from "./ports";

export interface ReconciliationPart {
	partNumber: number;
	etag: string;
	size?: number;
	/** Present for `/uploads` evidence; legacy list-parts responses omit it. */
	targetId?: string;
	ordinal?: number;
}

export type ListPartsEvidence = { kind: "verified"; parts: ReconciliationPart[] } | { kind: "assembled" } | { kind: "gone" };

/**
 * LSP-17796: raised when the list-parts endpoint answered but could NOT vouch for the manifest it
 * returned — the server hit a `MultipartPartsListingAnomalyError` (a part missing PartNumber/ETag/Size,
 * a truncated page with no continuation marker, a marker that did not advance) and degraded to a
 * placeholder `{ parts: [], anomalous: true }` response so the client's resume loop would not spin on a
 * 500.
 *
 * That placeholder MUST NOT reach any consumer as `{ kind: "verified", parts: [] }`. "Verified empty" is
 * a POSITIVE claim that S3 holds zero parts, and both apps' terminal-recovery policies act on it
 * destructively: `hasContiguousParts([], totalChunks)` is false for any real session, so the async policy
 * falls through to `markBytesGone` and the admin policy to `RESUME_OUTCOME.LOST` + local session
 * deletion. An unresolved manifest would then permanently discard locally-cached bytes that are very
 * likely still recoverable — strictly worse than the 500 the degrade replaced, which merely retried.
 *
 * Throwing at the port boundary (rather than inventing a third evidence kind) is deliberate: it means the
 * anomalous answer can never be MODELLED as evidence at all, so no current or future consumer of
 * `ListPartsEvidence` has to remember to special-case it. Every existing call site already routes a
 * listParts rejection to a non-destructive defer/retry:
 *   - `reconcilePersistedSession`'s probe → `{ kind: "defer", error }`
 *   - the shared driver's `decidePersistedTerminal` → `releasePersistedSessionForRetry`
 *   - contact-admin's `probeMultipartParts` → rethrows, per its documented "caller decides" contract.
 */
export class AmbiguousListPartsError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "AmbiguousListPartsError";
	}
}

/**
 * Maps a raw `/multipart/:uploadId/list-parts` response onto ListPartsEvidence, failing closed on any
 * answer the server could not vouch for. Shared by contact-async and contact-admin so the two apps
 * cannot drift on which server answers count as a positively verified manifest.
 */
export function listPartsEvidenceFromResponse(
	uploadId: string,
	response: { parts?: unknown; gone?: unknown; anomalous?: unknown; state?: unknown } | null | undefined
): ListPartsEvidence {
	if (response?.gone === true) return { kind: "gone" };
	if (response?.state === "assembled") return { kind: "assembled" };
	if (response?.state === "gone-unassembled") return { kind: "gone" };
	// Any TRUTHY anomalous flag fails closed, not just a literal `true`. `gone` is checked strictly
	// because guessing wrong there is merely a missed shortcut, but guessing wrong here costs the user
	// their bytes — a malformed flag is itself evidence the answer cannot be vouched for.
	if (response?.anomalous || response?.state === "anomalous") {
		throw new AmbiguousListPartsError(
			`ListParts for upload ${uploadId} returned a manifest the server could not vouch for (ambiguous — deferring)`
		);
	}
	if (!Array.isArray(response?.parts)) {
		throw new AmbiguousListPartsError(`ListParts for upload ${uploadId} returned no parts array (ambiguous — deferring)`);
	}
	const parts = (response.parts as Array<Record<string, unknown>>).map((part) => {
		// A non-object entry (null, a number, a string) is a malformed manifest, not this normaliser's
		// business: it is passed through UNTOUCHED so the downstream evidence readers reach exactly the
		// verdict they reached before the ordinal mirror existed. Reading `.partNumber` off it here
		// would throw a bare TypeError out of a function whose only declared failure is
		// `AmbiguousListPartsError`.
		if (!part || typeof part !== "object") return part as unknown as ReconciliationPart;
		// The chunked-object `/parts` arm reports `ordinal` (the `.part` object's declared ordinal) and
		// no `partNumber`; the two are the same 1-based number, so mirror it rather than teaching every
		// consumer a second field name.
		if (typeof part.partNumber !== "number" && typeof part.ordinal === "number") {
			return { ...part, partNumber: part.ordinal } as unknown as ReconciliationPart;
		}
		return part as unknown as ReconciliationPart;
	});
	return { kind: "verified", parts };
}

export type ReconciliationEvidence =
	| {
			kind: "local-gaps";
			missingBufferIndexes: number[];
			missingMetadataCount: number;
			listParts: ListPartsEvidence;
	  }
	| { kind: "read-failure"; error: unknown; listParts: ListPartsEvidence }
	| { kind: "terminal-data"; chunkIndexes: number[]; reason?: string };

export type ReconciliationTerminalDisposition =
	"remote-complete" | "remote-parts-landed" | "remote-gone" | "recovery-blocked" | "read-deferred" | "terminal-local-data";

/**
 * One evidence-precedence table shared by both upload apps.
 *
 * `transport` matters for exactly one verdict, and getting it wrong loses the user's video (review
 * C2). A contiguous 1..N VERIFIED listing means something different on the two lanes:
 *
 *   multipart       every part is up and the MPU is still open. The server's own AsyncWorker recovery
 *                   completes it, so the bytes are safe without the client: "remote-complete".
 *   chunked-object  the `.part` objects have landed and NOTHING on the server ever assembles them.
 *                   `assembleObjectFromParts` is reachable only as a dependency of `/complete`; the
 *                   sweep only DELETES. So a landed listing is not a finished upload — it is an
 *                   upload waiting for a `/complete` that only the client can send: "remote-parts-landed".
 *
 * Callers that omit `transport` keep exactly the multipart behaviour, so no legacy call site changes
 * meaning. The `assembled` answer is unconditionally "remote-complete" on both lanes — that one IS the
 * server saying the object exists.
 */
export function classifyReconciliationTerminalEvidence(
	evidence: ReconciliationEvidence,
	totalChunks: number,
	options?: { transport?: "multipart" | "chunked-object" | null }
): ReconciliationTerminalDisposition {
	if (evidence.kind === "terminal-data") return "terminal-local-data";
	if (evidence.listParts.kind === "assembled") return "remote-complete";
	if (evidence.listParts.kind === "gone") return "remote-gone";
	if (evidence.kind === "local-gaps") return "recovery-blocked";
	if (
		!hasContiguousParts(
			[...evidence.listParts.parts]
				.map((part) => ({ PartNumber: part.partNumber }))
				.sort((left, right) => left.PartNumber - right.PartNumber),
			totalChunks
		)
	) {
		return "read-deferred";
	}
	return options?.transport === "chunked-object" ? "remote-parts-landed" : "remote-complete";
}

/** Returns true only for an exact, pre-sorted 1..totalChunks part sequence. */
export function hasContiguousParts(parts: { PartNumber: number }[], totalChunks: number): boolean {
	if (!Number.isInteger(totalChunks) || totalChunks <= 0 || parts.length !== totalChunks) return false;
	for (let index = 0; index < totalChunks; index++) {
		if (parts[index]?.PartNumber !== index + 1) return false;
	}
	return true;
}

export type ReconciliationDataClassification = "available" | "missing" | "terminal";

export interface ReconciliationTerminalPolicy<Session, Outcome> {
	decide(context: { session: Session; evidence: ReconciliationEvidence }, lease?: UploadLease<Session>): Promise<Outcome>;
}

type ReconciliationResult<Meta, Outcome> =
	// `probedListParts` records whether THIS reconciliation already consulted the server's part
	// manifest. The driver's resume-only remote-complete probe exists solely for the intact-grid
	// path that returns ready WITHOUT probing; a second ListParts on the post-probe adoption path
	// is redundant (its `assembled` case was already the terminal policy's to decide) and hung the
	// single-shot ListParts harness in breakit-f.batch2-terminal-yield.spec.ts.
	| { kind: "ready"; allChunkMeta: Meta[]; unuploadedMeta: Meta[]; probedListParts: boolean }
	| { kind: "terminal"; outcome: Outcome }
	| { kind: "defer"; error: unknown }
	| { kind: "lease-lost" };

type ReconciliationTerminalDecision<Outcome> = { kind: "terminal"; outcome: Outcome } | { kind: "defer"; error: unknown };

interface ReconcilePersistedSessionOptions<Session, Meta, Data, Outcome> {
	session: Session;
	totalChunks?: number;
	store: {
		listChunkMeta(session: Session): Promise<Meta[]>;
		readChunkData(session: Session, chunkIndex: number): Promise<Data | undefined>;
		putChunkMeta(session: Session, chunk: Meta, options?: { ifAbsent?: boolean }): Promise<OwnershipWriteOutcome>;
	};
	lease: { owns(session: Session): Promise<boolean> };
	probe: { listParts(session: Session): Promise<ListPartsEvidence> };
	policy: ReconciliationTerminalPolicy<Session, Outcome>;
	meta: {
		chunkIndex(chunk: Meta): number;
		isUploaded(chunk: Meta): boolean;
		etag(chunk: Meta): string | null | undefined;
		adoptEtag(chunk: Meta, etag: string): Meta;
		createFromServerPart?(session: Session, chunkIndex: number, etag: string, size: number): Meta;
		needsUpload?(chunk: Meta): boolean;
	};
	classifyData?: (data: Data | undefined, chunk: Meta) => ReconciliationDataClassification;
	reconcileReadFailure?: boolean | ((error: unknown) => boolean);
	/** Probe an intact local grid when the previous pass was refused while server completion was settling. */
	forceProbe?: boolean;
}

function defaultDataClassification<Data>(data: Data | undefined): ReconciliationDataClassification {
	return data === undefined || data === null ? "missing" : "available";
}

export function malformedListPartsError(evidence: unknown, totalChunks: number): Error | undefined {
	if (!evidence || typeof evidence !== "object") {
		return new Error("ListParts returned malformed evidence");
	}
	const candidate = evidence as { kind?: unknown; parts?: unknown };
	if (candidate.kind === "gone" || candidate.kind === "assembled") {
		return "parts" in candidate ? new Error(`ListParts returned malformed ${candidate.kind} evidence`) : undefined;
	}
	if (candidate.kind !== "verified" || !Array.isArray(candidate.parts)) {
		return new Error("ListParts returned malformed evidence");
	}

	// A verified ListParts response either reports sizes for its parts or reports none at all (both
	// apps' adapters pass the server's rows straight through). A response that reports a size for
	// SOME parts and leaves others undefined is internally inconsistent evidence, and it is a
	// LOAD-BEARING inconsistency: listPartsByteTotalMismatchError only compares byte totals when
	// EVERY part carries a valid positive size, so one sizeless part in an otherwise sized response
	// silently disables the byte-total cross-check while still looking verified — the mismatch is
	// never reported and never deferred (round 81 / LSP-17735).
	const someSizesReported = candidate.parts.some(
		(part) => !!part && typeof part === "object" && (part as { size?: unknown }).size !== undefined
	);
	const seenPartNumbers = new Set<number>();
	for (const part of candidate.parts) {
		if (!part || typeof part !== "object") {
			return new Error("ListParts returned a malformed part");
		}
		const candidatePart = part as {
			partNumber?: unknown;
			etag?: unknown;
			size?: unknown;
			targetId?: unknown;
			ordinal?: unknown;
		};
		if (
			typeof candidatePart.partNumber !== "number" ||
			!Number.isInteger(candidatePart.partNumber) ||
			candidatePart.partNumber < 1 ||
			candidatePart.partNumber > totalChunks ||
			seenPartNumbers.has(candidatePart.partNumber) ||
			!isValidEtag(candidatePart.etag) ||
			((candidatePart.targetId !== undefined || candidatePart.ordinal !== undefined) &&
				(typeof candidatePart.targetId !== "string" ||
					candidatePart.targetId.trim().length === 0 ||
					!Number.isSafeInteger(candidatePart.ordinal) ||
					(candidatePart.ordinal as number) !== candidatePart.partNumber)) ||
			// An absent size is malformed only once this same response has proven it reports sizes;
			// a uniformly sizeless response stays acceptable evidence (see someSizesReported above).
			(candidatePart.size === undefined
				? someSizesReported
				: !Number.isSafeInteger(candidatePart.size) || (candidatePart.size as number) <= 0)
		) {
			return new Error("ListParts returned a malformed part");
		}
		seenPartNumbers.add(candidatePart.partNumber);
	}
	return undefined;
}

// Both real terminal policies (contact-async's asyncTerminalRecoveryPolicy, contact-admin's
// recoverAdminTerminalSession) decide handoff-vs-lost purely from part COUNT/contiguity — a
// ListParts response that is contiguous 1..totalChunks with valid ETags but whose part sizes
// contradict session.fileSize (a stale/partially-overwritten multipart upload, or a reused
// uploadId with a different byte layout) must never reach either policy. The local-gaps evidence
// producer below already defers on this before adopting server parts; this is the SAME check,
// hoisted to the shared choke point every evidence kind funnels through, so a producer that skips
// it (round 49 / LSP-17735: the read-failure producer did) cannot let mismatched evidence through.
function listPartsByteTotalMismatchError(listParts: ListPartsEvidence, totalChunks: number, expectedFileSize: unknown): Error | undefined {
	if (listParts.kind !== "verified") return undefined;
	// A short listing carries no immutability argument (a later pass can legitimately see more
	// committed parts), and a listing that does not report every part's size gives nothing to total,
	// so neither can be cross-checked here at all — both stay the policy's call.
	if (listParts.parts.length !== totalChunks || !listParts.parts.every((part) => Number.isSafeInteger(part.size) && part.size! > 0)) {
		return undefined;
	}
	const assembledByteTotal = listParts.parts.reduce((totalBytes, part) => totalBytes + part.size!, 0);
	// An UNVERIFIABLE expectedFileSize must never return the same `undefined` a genuine match returns.
	// Folding it into the guard above silently disabled the only cross-check on the server listing and
	// dropped the session through to policy.decide carrying the RAW listParts evidence — and both real
	// terminal policies read a contiguous listing as "the server already holds the whole object"
	// (hasContiguousParts), so a session whose completeness can no longer be checked AT ALL was handed
	// off / had its only local copy discarded, the exact round 49 hazard. session.fileSize is written
	// once from file.size when the row is created and is never rewritten (the driver only COMPARES it
	// against a reattached file), so an unusable value is as immutable as a genuine mismatch: deferring
	// would re-derive the identical verdict forever (round 53 / round 81). Fail CLOSED, exactly like the
	// sibling validator inspectPersistedChunkIntegrity's "unverifiable-file-size" (round 50 / LSP-17735).
	if (typeof expectedFileSize !== "number" || !Number.isSafeInteger(expectedFileSize) || expectedFileSize < 0) {
		return new Error(
			`Cannot reconcile multipart metadata: server parts total ${assembledByteTotal} bytes; session fileSize ${String(expectedFileSize)} is unverifiable`
		);
	}
	if (assembledByteTotal === expectedFileSize) return undefined;
	return new Error(`Cannot reconcile multipart metadata: server parts total ${assembledByteTotal} bytes; expected ${expectedFileSize}`);
}

// A byte-total mismatch on a VERIFIED listing that already holds `totalChunks` parts is built from
// two IMMUTABLE facts: S3 has committed exactly the expected number of parts for this uploadId (a
// committed part is immutable unless it is re-PUT, which cannot happen on any path that reaches
// this choke point — those are precisely the paths whose local buffers/metadata are gone), and
// those parts' sizes do not sum to the session's own fileSize. No later reconciliation pass can
// re-derive a different verdict, so deferring made the drain re-pick the identical unrepairable
// session forever instead of surfacing the truthful needs-attention state — the same infinite-defer
// class round 53 fixed for the sibling per-chunk adoption branch (round 81 / LSP-17735).
//
// The unverifiable-fileSize verdict listPartsByteTotalMismatchError also returns (round 82) routes
// here for the same reason: `session.fileSize` is likewise written once and never rewritten, so a
// session we cannot cross-check today we cannot cross-check on any later pass either, and "we cannot
// trust this session is intact" is the same verdict as "we proved it is not".
//
// It must be terminal AS LOSS, never as the listParts-bearing evidence: both real terminal policies
// decide handoff purely from `hasContiguousParts`, so the contiguous-but-byte-inconsistent listing
// would be read as "the server already holds the whole object" and would hand off / delete the only
// local copy of a file the server demonstrably does NOT hold intact (round 49). `terminal-data` is
// the one evidence kind both policies treat as unrecoverable loss without consulting ListParts
// (async: markBytesGone; admin: RESUME_OUTCOME.LOST without deleting local state).
async function decideUnrecoverableByteTotal<Session, Outcome>(options: {
	session: Session;
	totalChunks: number;
	reason: string;
	policy: ReconciliationTerminalPolicy<Session, Outcome>;
	lease?: UploadLease<Session>;
}): Promise<{ kind: "terminal"; outcome: Outcome }> {
	const context = {
		session: options.session,
		evidence: {
			kind: "terminal-data" as const,
			chunkIndexes: Array.from({ length: options.totalChunks }, (_value, chunkIndex) => chunkIndex),
			reason: options.reason
		}
	};
	const outcome = options.lease ? await options.policy.decide(context, options.lease) : await options.policy.decide(context);
	return { kind: "terminal", outcome };
}

export async function decideReconciliationTerminal<Session, Outcome>(options: {
	session: Session;
	totalChunks: number;
	evidence: ReconciliationEvidence;
	policy: ReconciliationTerminalPolicy<Session, Outcome>;
	lease?: UploadLease<Session>;
}): Promise<ReconciliationTerminalDecision<Outcome>> {
	if ("listParts" in options.evidence) {
		const malformedEvidence = malformedListPartsError(options.evidence.listParts, options.totalChunks);
		if (malformedEvidence) return { kind: "defer", error: malformedEvidence };
		const expectedFileSize = (options.session as Session & { fileSize?: unknown }).fileSize;
		const byteTotalMismatch = listPartsByteTotalMismatchError(options.evidence.listParts, options.totalChunks, expectedFileSize);
		if (byteTotalMismatch) {
			return decideUnrecoverableByteTotal({
				session: options.session,
				totalChunks: options.totalChunks,
				reason: byteTotalMismatch.message,
				policy: options.policy,
				lease: options.lease
			});
		}
	}
	const context = { session: options.session, evidence: options.evidence };
	const outcome = options.lease ? await options.policy.decide(context, options.lease) : await options.policy.decide(context);
	return { kind: "terminal", outcome };
}

export async function reconcilePersistedSession<Session, Meta, Data, Outcome>(
	options: ReconcilePersistedSessionOptions<Session, Meta, Data, Outcome>
): Promise<ReconciliationResult<Meta, Outcome>> {
	const totalChunks = options.totalChunks ?? (options.session as Session & { totalChunks: number }).totalChunks;
	if (typeof totalChunks !== "number" || !Number.isInteger(totalChunks) || totalChunks < 1) {
		// A session without a positive-integer totalChunks cannot be safely reconciled ON ANY PATH:
		// NaN or fractional accounting would route an otherwise healthy session into the terminal
		// policy (§6.1 — ambiguous never terminal), a zero/negative count would report an EMPTY
		// session "ready" even though shared completion guards correctly reject it via
		// hasValidMultipartParts, while the reconcileReadFailure branch would pass a degenerate count
		// to a terminal policy whose handoff-vs-lost decision compares ListParts against it. This check must
		// run BEFORE the read-failure branch below, not just before the local-gaps accounting.
		// Defer so a later pass (or repair) decides.
		return {
			kind: "defer",
			error: new Error("Cannot reconcile a persisted session without a positive integer totalChunks")
		};
	}

	const ownsLease = (): Promise<boolean> => options.lease.owns(options.session);
	if (!(await ownsLease())) return { kind: "lease-lost" };

	let allChunkMeta: Meta[];
	try {
		allChunkMeta = await options.store.listChunkMeta(options.session);
	} catch (error) {
		const shouldReconcileReadFailure =
			typeof options.reconcileReadFailure === "function" ? options.reconcileReadFailure(error) : options.reconcileReadFailure;
		if (!shouldReconcileReadFailure) return { kind: "defer", error };
		if (!(await ownsLease())) return { kind: "lease-lost" };
		let listParts: ListPartsEvidence;
		try {
			listParts = await options.probe.listParts(options.session);
		} catch (probeError) {
			return { kind: "defer", error: probeError };
		}
		if (!(await ownsLease())) return { kind: "lease-lost" };
		return decideReconciliationTerminal({
			session: options.session,
			totalChunks,
			evidence: { kind: "read-failure", error, listParts },
			policy: options.policy
		});
	}
	if (!(await ownsLease())) return { kind: "lease-lost" };
	allChunkMeta = allChunkMeta.filter((chunk) => {
		const chunkIndex = options.meta.chunkIndex(chunk);
		return Number.isInteger(chunkIndex) && chunkIndex >= 0 && chunkIndex < totalChunks;
	});

	const classifyData = options.classifyData ?? defaultDataClassification;
	const needsUpload = (chunk: Meta): boolean => {
		const etag = options.meta.etag(chunk);
		if (typeof etag === "string" && !isValidEtag(etag)) return true;
		return options.meta.needsUpload?.(chunk) ?? (!options.meta.isUploaded(chunk) || !isValidEtag(etag));
	};
	const unuploadedMeta = allChunkMeta.filter(needsUpload);
	const missingBufferIndexes: number[] = [];
	const terminalDataIndexes: number[] = [];
	for (const chunk of unuploadedMeta) {
		try {
			const data = await options.store.readChunkData(options.session, options.meta.chunkIndex(chunk));
			const classification = classifyData(data, chunk);
			if (classification !== "available" && classification !== "missing" && classification !== "terminal") {
				return {
					kind: "defer",
					error: new Error("Invalid reconciliation data classification")
				};
			}
			if (classification === "missing") missingBufferIndexes.push(options.meta.chunkIndex(chunk));
			if (classification === "terminal") terminalDataIndexes.push(options.meta.chunkIndex(chunk));
		} catch (error) {
			return { kind: "defer", error };
		}
		if (!(await ownsLease())) return { kind: "lease-lost" };
	}

	if (terminalDataIndexes.length > 0) {
		const initialChunkIndexes = allChunkMeta.map((chunk) => options.meta.chunkIndex(chunk));
		if (new Set(initialChunkIndexes).size !== initialChunkIndexes.length) {
			return {
				kind: "defer",
				error: new Error("Cannot reconcile duplicate local chunk metadata indexes")
			};
		}
		return decideReconciliationTerminal({
			session: options.session,
			totalChunks,
			evidence: { kind: "terminal-data", chunkIndexes: terminalDataIndexes },
			policy: options.policy
		});
	}

	const coveredChunkIndexes = new Set(
		allChunkMeta
			.map((chunk) => options.meta.chunkIndex(chunk))
			.filter((chunkIndex) => Number.isInteger(chunkIndex) && chunkIndex >= 0 && chunkIndex < totalChunks)
	);
	const missingMetadataIndexes = Array.from({ length: totalChunks }, (_value, chunkIndex) => chunkIndex).filter(
		(chunkIndex) => !coveredChunkIndexes.has(chunkIndex)
	);
	let missingMetadataCount = missingMetadataIndexes.length;
	let probedListParts = false;
	if (missingBufferIndexes.length === 0 && missingMetadataCount === 0 && options.forceProbe) {
		let listParts: ListPartsEvidence;
		try {
			listParts = await options.probe.listParts(options.session);
		} catch (error) {
			return { kind: "defer", error };
		}
		if (!(await ownsLease())) return { kind: "lease-lost" };
		const malformedEvidence = malformedListPartsError(listParts, totalChunks);
		if (malformedEvidence) return { kind: "defer", error: malformedEvidence };
		probedListParts = true;
		if (listParts.kind === "assembled") {
			return decideReconciliationTerminal({
				session: options.session,
				totalChunks,
				evidence: { kind: "local-gaps", missingBufferIndexes: [], missingMetadataCount: 0, listParts },
				policy: options.policy
			});
		}
	}
	if (missingBufferIndexes.length === 0 && missingMetadataCount === 0) {
		if (coveredChunkIndexes.size !== allChunkMeta.length) {
			return {
				kind: "defer",
				error: new Error("Cannot reconcile duplicate local chunk metadata indexes")
			};
		}
		return { kind: "ready", allChunkMeta, unuploadedMeta, probedListParts };
	}

	let listParts: ListPartsEvidence;
	try {
		listParts = await options.probe.listParts(options.session);
	} catch (error) {
		return { kind: "defer", error };
	}
	if (!(await ownsLease())) return { kind: "lease-lost" };
	const malformedEvidence = malformedListPartsError(listParts, totalChunks);
	if (malformedEvidence) return { kind: "defer", error: malformedEvidence };

	// Hoisted above the byte-total pre-flight AND the synthesis loop below so BOTH consult the same
	// verdict — an invalid local chunk size must abandon synthesis entirely, not just the byte-total
	// check that happens to run first (round 54 / LSP-17735).
	let hasInvalidLocalSize = false;
	if (listParts.kind === "verified") {
		const localChunkIndexes = allChunkMeta.map((chunk) => options.meta.chunkIndex(chunk));
		if (new Set(localChunkIndexes).size !== localChunkIndexes.length) {
			return {
				kind: "defer",
				error: new Error("Cannot reconcile duplicate local chunk metadata indexes")
			};
		}
		const expectedFileSize = (options.session as Session & { fileSize?: unknown }).fileSize;
		// Same immutable pair of facts as the shared choke point below, reached here BEFORE any server
		// ETag is adopted or any row is synthesized from server evidence: terminal-as-loss, never a
		// defer that the drain would re-derive identically forever (round 81 / LSP-17735).
		const byteTotalMismatch = listPartsByteTotalMismatchError(listParts, totalChunks, expectedFileSize);
		if (byteTotalMismatch) {
			return decideUnrecoverableByteTotal({
				session: options.session,
				totalChunks,
				reason: byteTotalMismatch.message,
				policy: options.policy
			});
		}
		const partsByIndex = new Map(listParts.parts.map((part) => [part.partNumber - 1, part]));
		if (
			missingMetadataIndexes.length > 0 &&
			options.meta.createFromServerPart &&
			typeof expectedFileSize === "number" &&
			Number.isSafeInteger(expectedFileSize) &&
			expectedFileSize >= 0
		) {
			const reconciledSizesByIndex = new Map<number, number>();
			// An invalid local chunk size (absent/zero/negative/non-integer) is a permanent fact —
			// FileUploadChunkMeta.size is written once, from the buffer's own byteLength, at persist
			// time, and no writer in either app ever rewrites it. No retry/reconnect/lease-handover/
			// ListParts response can change it. This exact predicate is ALREADY treated as terminal by
			// validatePersistedChunkByteIntegrity (driverChunkLoop.ts) whenever the local metadata grid
			// happens to be complete — but when metadata is short by at least one row too (this
			// synthesis pre-flight, triggered by missingMetadataIndexes.length > 0), deferring here left
			// no path back to that terminal check: the row never reaches "ready", so
			// validatePersistedChunkByteIntegrity is never even called (round 54 / LSP-17735). Abandon
			// synthesis (skip the reconciledSizesByIndex accumulation and the loops that depend on it)
			// instead of deferring — missingMetadataIndexes then stays non-empty and this chunk still
			// falls through to the SAME local-gaps -> decideReconciliationTerminal choke point round 53
			// wired the adoption step's sibling condition into.
			for (const chunk of allChunkMeta) {
				const chunkSize = (chunk as Meta & { size?: unknown }).size;
				if (!Number.isSafeInteger(chunkSize) || (chunkSize as number) <= 0) {
					hasInvalidLocalSize = true;
					break;
				}
				reconciledSizesByIndex.set(options.meta.chunkIndex(chunk), chunkSize as number);
			}
			if (hasInvalidLocalSize) {
				reconciledSizesByIndex.clear();
			}
			for (const chunkIndex of hasInvalidLocalSize ? [] : missingMetadataIndexes) {
				const serverSize = partsByIndex.get(chunkIndex)?.size;
				if (Number.isSafeInteger(serverSize) && serverSize! > 0) {
					reconciledSizesByIndex.set(chunkIndex, serverSize!);
				}
			}
			if (!hasInvalidLocalSize && reconciledSizesByIndex.size === totalChunks) {
				const reconciledByteTotal = [...reconciledSizesByIndex.values()].reduce(
					(totalBytes, chunkBytes) => totalBytes + chunkBytes,
					0
				);
				if (reconciledByteTotal !== expectedFileSize) {
					return {
						kind: "defer",
						error: new Error(
							`Cannot reconcile multipart metadata: combined parts total ${reconciledByteTotal} bytes; expected ${expectedFileSize}`
						)
					};
				}
			}
		}
		for (const chunkIndex of missingBufferIndexes) {
			const serverPart = partsByIndex.get(chunkIndex);
			if (!serverPart || !isValidEtag(serverPart.etag)) continue;
			if (!(await ownsLease())) return { kind: "lease-lost" };
			// Re-read immediately before writing, not from the outer allChunkMeta captured earlier in
			// this call. The SAME owner can legitimately have a live chunk-loop concurrently checkpoint
			// this exact chunk (e.g. a stale-but-not-yet-cancelled resume attempt racing a fresh one via
			// the driver's cooperative-cancellation dedup) between our earlier read and this adoption
			// write; adopting from a stale snapshot would silently clobber that newer checkpoint with
			// this listParts-derived etag. If a concurrent writer already marked it uploaded, leave it.
			let freshChunkMeta: Meta[];
			try {
				freshChunkMeta = await options.store.listChunkMeta(options.session);
			} catch (error) {
				return { kind: "defer", error };
			}
			if (!(await ownsLease())) return { kind: "lease-lost" };
			const chunk = freshChunkMeta.find((item) => options.meta.chunkIndex(item) === chunkIndex);
			// A chunk can legitimately have isUploaded=true with an invalid/lost etag (the exact case
			// this adoption step exists to repair), so the skip guard must be "does it already have a
			// VALID etag" (a concurrent writer already adopted/checkpointed one) - isUploaded alone is
			// not sufficient reason to skip adoption.
			if (!chunk || isValidEtag(options.meta.etag(chunk))) continue;
			const persistedChunkSize = (chunk as Meta & { size?: unknown }).size;
			if (typeof serverPart.size === "number" && typeof persistedChunkSize === "number" && persistedChunkSize !== serverPart.size) {
				// A server part whose size contradicts the persisted chunk's own size can be neither
				// adopted (the etag would not actually cover our bytes) nor ever re-PUT (the local
				// buffer is gone — this loop only runs for missingBufferIndexes). Deferring here used
				// to demote the row to "pending" with no path back: retrying can never change either
				// immutable fact, so the drain re-picked the identical unrepairable session forever
				// instead of reaching the terminal "bytes-gone" verdict its own sibling (a server part
				// simply ABSENT for this chunk) already reaches. `continue` instead of deferring — the
				// chunk stays unadopted and therefore stays in missingBufferIndexes, falling through to
				// the SAME local-gaps -> decideReconciliationTerminal choke point that already handles
				// "no usable server part for this missing-buffer chunk" (round 53 / LSP-17735).
				continue;
			}
			let adoptionOutcome: OwnershipWriteOutcome;
			try {
				adoptionOutcome = await options.store.putChunkMeta(options.session, options.meta.adoptEtag(chunk, serverPart.etag));
			} catch (error) {
				return { kind: "defer", error };
			}
			if (adoptionOutcome === "skipped-not-owned") return { kind: "lease-lost" };
		}
		// Gated on !hasInvalidLocalSize too — an invalid local chunk size elsewhere in the grid means
		// this whole session's local metadata cannot be trusted enough to synthesize new rows from
		// server evidence; abandon synthesis and let missingMetadataIndexes stay non-empty so this
		// falls through to the local-gaps -> decideReconciliationTerminal choke point instead
		// (round 54 / LSP-17735).
		if (options.meta.createFromServerPart && !hasInvalidLocalSize) {
			for (const chunkIndex of missingMetadataIndexes) {
				const serverPart = partsByIndex.get(chunkIndex);
				if (
					!serverPart ||
					!isValidEtag(serverPart.etag) ||
					typeof serverPart.size !== "number" ||
					!Number.isInteger(serverPart.size) ||
					serverPart.size <= 0
				) {
					continue;
				}
				if (!(await ownsLease())) return { kind: "lease-lost" };
				let freshChunkMeta: Meta[];
				try {
					freshChunkMeta = await options.store.listChunkMeta(options.session);
				} catch (error) {
					return { kind: "defer", error };
				}
				if (!(await ownsLease())) return { kind: "lease-lost" };
				const concurrentChunk = freshChunkMeta.find((chunk) => options.meta.chunkIndex(chunk) === chunkIndex);
				if (concurrentChunk) continue;
				let synthesizedChunk: Meta;
				try {
					synthesizedChunk = options.meta.createFromServerPart(options.session, chunkIndex, serverPart.etag, serverPart.size);
				} catch (error) {
					return { kind: "defer", error };
				}
				try {
					if (
						options.meta.chunkIndex(synthesizedChunk) !== chunkIndex ||
						options.meta.etag(synthesizedChunk) !== serverPart.etag
					) {
						return {
							kind: "defer",
							error: new Error(`Cannot reconcile synthesized metadata identity for chunk ${chunkIndex}`)
						};
					}
					if ((synthesizedChunk as Meta & { size?: unknown }).size !== serverPart.size || needsUpload(synthesizedChunk)) {
						return {
							kind: "defer",
							error: new Error(`Cannot reconcile synthesized metadata shape for chunk ${chunkIndex}`)
						};
					}
				} catch (error) {
					return { kind: "defer", error };
				}
				let synthesisOutcome: OwnershipWriteOutcome;
				try {
					synthesisOutcome = await options.store.putChunkMeta(options.session, synthesizedChunk, { ifAbsent: true });
				} catch (error) {
					return { kind: "defer", error };
				}
				if (synthesisOutcome === "skipped-not-owned") return { kind: "lease-lost" };
			}
		}
		if (!(await ownsLease())) return { kind: "lease-lost" };
		try {
			allChunkMeta = await options.store.listChunkMeta(options.session);
			allChunkMeta = allChunkMeta.filter((chunk) => {
				const chunkIndex = options.meta.chunkIndex(chunk);
				return Number.isInteger(chunkIndex) && chunkIndex >= 0 && chunkIndex < totalChunks;
			});
		} catch (error) {
			return { kind: "defer", error };
		}
		if (!(await ownsLease())) return { kind: "lease-lost" };
		const refreshedChunkIndexes = new Set(allChunkMeta.map((chunk) => options.meta.chunkIndex(chunk)));
		missingMetadataCount = missingMetadataIndexes.filter((chunkIndex) => !refreshedChunkIndexes.has(chunkIndex)).length;
	}

	const finalChunkIndexes = allChunkMeta.map((chunk) => options.meta.chunkIndex(chunk));
	if (new Set(finalChunkIndexes).size !== finalChunkIndexes.length) {
		return {
			kind: "defer",
			error: new Error("Cannot reconcile duplicate local chunk metadata indexes")
		};
	}
	const remainingUnuploaded = allChunkMeta.filter(needsUpload);
	const remainingMissingBuffers = missingBufferIndexes.filter((chunkIndex) =>
		remainingUnuploaded.some((chunk) => options.meta.chunkIndex(chunk) === chunkIndex)
	);
	if (remainingMissingBuffers.length === 0 && missingMetadataCount === 0) {
		return { kind: "ready", allChunkMeta, unuploadedMeta: remainingUnuploaded, probedListParts: true };
	}

	// The post-adoption meta re-list above is an awaited IDB read — the lease can be stolen while
	// it is in flight. The terminal policy performs the most destructive session writes in either
	// app (admin deletes the local session; async durably marks bytes-gone), so ownership must be
	// re-verified immediately before handing over, exactly like every other awaited step (§6.8).
	if (!(await ownsLease())) return { kind: "lease-lost" };
	return decideReconciliationTerminal({
		session: options.session,
		totalChunks,
		evidence: {
			kind: "local-gaps",
			missingBufferIndexes: remainingMissingBuffers,
			missingMetadataCount,
			listParts
		},
		policy: options.policy
	});
}
